import { Button, ButtonGroup, Container, Divider } from "@material-ui/core";
import { Grid, Tooltip } from "@mui/material";
import axios from "axios";
import {
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  SecondaryButton,
  SkeletonComponent,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import ContractDetailForm from "./Components/ContractDetailForm";
import SupportingDocuments from "./Components/SupportingDocuments";

const ContractDetail = () => {
  const classes = globalStyles();
  const history = useHistory();
  const { id } = useParams();
  const { state } = useLocation();
  const isFirstLoad = useRef(true);
  const { approval_status, is_active_flag, category } = state?.customer;
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const isSongContract = category === "song";
  const [selectedMenu, setSelectedMenu] = useState(1);
  const [loadingPage, setLoadingPage] = useState(false);
  const [userRole, setUserRole] = useState();
  const [pageDetail, setPageDetail] = useState({
    contract: {},
  });
  const [queryParamsDocuments, setQueryParamsDocuments] = useState({
    page: 1,
    size: 10,
    type: "-created_at",
    search: "",
  });
  const [dataTableDocuments, setDataTableDocuments] = useState([]);
  const [loadingTableSong, setLoadingTableSong] = useState(true);
  const [dataTableSong, setDataTableSong] = useState([]);
  const [queryParamsSong, setQueryParamsSong] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPageSong, setTableTotalPageSong] = useState(1);
  const [loadingTableAdvance, setLoadingTableAdvance] = useState(true);
  const [dataTableAdvance, setDataTableAdvance] = useState([]);
  const [queryParamsAdvance, setQueryParamsAdvance] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPageAdvance, setTableTotalPageAdvance] = useState(1);
  const [loadingTableAgents, setLoadingTableAgents] = useState(true);
  const [dataTableAgents, setDataTableAgents] = useState([]);
  const [queryParamsAgents, setQueryParamsAgents] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPageAgents, setTableTotalPageAgents] = useState(1);

  const disabledEdit = approval_status === "approved";

  const handleDeactivateContract = () =>
    ModalWarning(
      "Are you sure you want to deactivate this contract?",
      "Deactivate Contract"
    ).then(res => res?.isConfirmed && deactivateContract());
  const handleChangeQueryParamsDocuments = (value, key) => {
    setQueryParamsDocuments(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleChangeQueryParamsSong = (value, key) => {
    setQueryParamsSong(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeQueryParamsAgents = (value, key) => {
    setQueryParamsAgents(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeQueryParamsAdvance = (value, key) => {
    setQueryParamsAdvance(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getMe = async () => {
    try {
      const roleId = localStorage?.getItem("role_id");
      const res = await axios.get(`${hardBaseUrl}/me/${roleId}`, { headers });
      setUserRole(res?.data?.data?.role?.roles_for);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDetail = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/contract/${
          isSongContract ? "" : "composer-main/"
        }${id}`,
        { headers }
      );
      const { data } = res?.data;
      const modifiedData = {
        ...data,
        songs: data?.songs
          ? data?.songs?.map(song => ({
              ...song,
              composers: song.composers?.map(composer => ({
                ...composer,
                balance: composer?.song_balance?.balance,
                start_period: composer?.song_balance?.start_period,
                end_period: composer?.song_balance?.end_period,
              })),
            }))
          : [],
        agents: data?.agents || [],
      };
      setPageDetail(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/contract-document/${state.customer.contract_id}`,
        { headers, params: queryParamsDocuments }
      );
      setDataTableDocuments(res?.data?.data || []);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParamsDocuments]
  );
  const getDataTableSong = async () => {
    try {
      setLoadingTableSong(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/contract/${id}/songs`,
        {
          headers,
          params: queryParamsSong,
        }
      );
      const { data, meta } = res?.data;
      setDataTableSong(data?.songs || []);
      const pageCount = getTotalPage(meta?.total, queryParamsSong?.size);
      setTableTotalPageSong(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingTableSong(false);
    }
  };
  const getDataTableAgents = async () => {
    try {
      setLoadingTableAgents(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/contract/${id}/agents`,
        {
          headers,
          params: queryParamsAgents,
        }
      );
      const { data, meta } = res?.data;
      setDataTableAgents(data?.agents || []);
      const pageCount = getTotalPage(meta?.total, queryParamsAgents?.size);
      setTableTotalPageAgents(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingTableAgents(false);
    }
  };
  const getDataTableAdvance = async () => {
    try {
      setLoadingTableAdvance(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/contract/${id}/advances`,
        {
          headers,
          params: queryParamsAdvance,
        }
      );
      const { data, meta } = res?.data;
      const advances = (data?.advances || []).map(advance => ({
        ...advance,
        composers: (advance.composers || []).map(composer => ({
          ...composer,
          balance: composer.song_balance?.balance ?? 0,
        })),
      }));

      setDataTableAdvance(advances || []);
      const pageCount = getTotalPage(meta?.total, queryParamsAdvance?.size);
      setTableTotalPageAdvance(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingTableAdvance(false);
    }
  };
  const deactivateContract = async () => {
    try {
      setLoadingPage(true);
      await axios.put(
        `${hardBaseUrl}/publisher/contract/set-inactive/${id}`,
        {},
        { headers }
      );
      ModalSuccess("Status Has Been Updated").then(() => history.goBack());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [
      getMe(),
      getDetail(),
      getDataTable(),
      getDataTableSong(),
      getDataTableAdvance(),
      getDataTableAgents(),
    ];

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (!isFirstLoad?.current) {
      getDataTableSong();
    }
  }, [queryParamsSong]);
  useEffect(() => {
    if (!isFirstLoad?.current) {
      getDataTableAdvance();
    }
  }, [queryParamsAdvance]);
  useEffect(() => {
    if (!isFirstLoad?.current) {
      getDataTableAgents();
    }
  }, [queryParamsAgents]);

  useEffect(() => {
    const isSearching = Boolean(queryParamsDocuments?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      if (!isFirstLoad.current) {
        getDataTable();
      }
    }
  }, [queryParamsDocuments, debounceDataTable]);

  return (
    <Page className={classes.root} title="Contract Detail">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title="Contract Detail"
            breadcrumbData={breadcrumbData}
            backButton
          />
          <Grid container justifyContent="space-between" mt="20px">
            <Grid item>
              <ButtonGroup>
                {menuContract?.map(({ title, id }) => {
                  const selected = id === selectedMenu;
                  return (
                    <Button
                      className={
                        selected
                          ? classes.groupButtonSelected
                          : classes?.groupButton
                      }
                      key={id}
                      onClick={() => {
                        setSelectedMenu(id);
                      }}
                    >
                      {title}
                    </Button>
                  );
                })}
              </ButtonGroup>
            </Grid>
            <Grid item>
              <Grid container columnSpacing={1}>
                <Grid item>
                  {is_active_flag && (
                    <PrimaryButton
                      label="Deactivate"
                      onClick={handleDeactivateContract}
                    />
                  )}
                </Grid>
                <Tooltip title={disabledEdit && "Contract is not editable"}>
                  <Grid item>
                    <SecondaryButton
                      label="Edit Contract"
                      onClick={() =>
                        history.push({
                          pathname: `/admin/kontrak-composer/edit/${id}`,
                          state: { customer: state?.customer },
                        })
                      }
                      disabled={disabledEdit}
                    />
                  </Grid>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          {selectedMenu === 1 ? (
            <ContractDetailForm
              preload={state?.customer}
              pageDetail={pageDetail}
              userRole={userRole}
              getDetail={fetchData}
              setLoadingPage={setLoadingPage}
              dataTableSong={dataTableSong}
              loadingTableSong={loadingTableSong}
              queryParamsSong={queryParamsSong}
              handleChangeQueryParamsSong={handleChangeQueryParamsSong}
              tableTotalPageSong={tableTotalPageSong}
              dataTableAdvance={dataTableAdvance}
              loadingTableAdvance={loadingTableAdvance}
              queryParamsAdvance={queryParamsAdvance}
              handleChangeQueryParamsAdvance={handleChangeQueryParamsAdvance}
              tableTotalPageAdvance={tableTotalPageAdvance}
              dataTableAgents={dataTableAgents}
              loadingTableAgents={loadingTableAgents}
              queryParamsAgents={queryParamsAgents}
              handleChangeQueryParamsAgents={handleChangeQueryParamsAgents}
              tableTotalPageAgents={tableTotalPageAgents}
            />
          ) : (
            <SupportingDocuments
              idKontrak={state.customer.contract_id}
              queryParams={queryParamsDocuments}
              handleChangeQueryParams={handleChangeQueryParamsDocuments}
              getDataTable={getDataTable}
              dataTable={dataTableDocuments}
            />
          )}
        </Container>
      )}
    </Page>
  );
};
const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Contract",
    link: "/admin/kontrak-composer",
  },
  {
    label: "Contract Detail",
    active: true,
  },
];
const menuContract = [
  {
    id: 1,
    title: "Detail",
  },
  {
    id: 2,
    title: "Supporting Document",
  },
];

export default ContractDetail;
